/**
 * Demo Page - Chapter 1 Preview Experience
 * 
 * Three main sections:
 * 1. Hero - Reused from features page with book showcase
 * 2. Meet Mila the Mouse - Character introductions (2x2 grid)
 * 3. Ready to Begin CTA - Large signup button with feature badges
 * 
 * Purpose: Drive conversions by showcasing Chapter 1 preview
 * Flow: Demo → Signup → TOC (with locked chapters) → Chapter 1 → End screen CTA
 */

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Play, CheckCircle, Users, Sparkles, GraduationCap, BookOpen } from "lucide-react";

// Character data - Demo page specific (Mila the Mouse)
const characters = [
  {
    name: "Mila the Mouse",
    description: "Curious and energetic, Mila leads the adventure and learns alongside young readers.",
    emoji: "🐭"
  },
  {
    name: "Pip the Porcupine",
    description: "Thoughtful and helpful, Pip helps friends think through challenges.",
    emoji: "🦔"
  },
  {
    name: "Bella the Bunny",
    description: "Gentle and caring, Bella shows how to be kind and compassionate.",
    emoji: "🐰"
  },
  {
    name: "Grandpa Tortoise",
    description: "Wise and patient, Grandpa shares wisdom with gentle guidance.",
    emoji: "🐢"
  }
];

// Inline Demo Character Card Component
function DemoCharacterCard({ name, description, emoji }: { name: string; description: string; emoji: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="h-full"
    >
      <div className="border-2 border-forest-200 hover:border-forest-300 hover:shadow-lg transition-all duration-300 h-full bg-gradient-to-br from-white to-cream-50 rounded-lg p-6 text-center">
        {/* Character Illustration Placeholder */}
        <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-forest-100 flex items-center justify-center text-5xl">
          {emoji}
        </div>
        
        {/* Character Name */}
        <h3 className="text-xl font-bold text-forest-900 mb-2">{name}</h3>
        
        {/* Character Description */}
        <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
      </div>
    </motion.div>
  );
}

export default function Demo() {
  // Function to scroll to top when buttons are clicked
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-cream-50">
      {/* Section 1: Hero (Reused from features page) */}
      <section className="relative pt-20 pb-16 text-white overflow-hidden" style={{ background: "linear-gradient(135deg, hsl(150 65% 30%) 0%, hsl(45 90% 45%) 100%)" }}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 border border-white/20 rounded-full"></div>
          <div className="absolute top-20 right-20 w-24 h-24 bg-white/10 rounded-full"></div>
          <div className="absolute bottom-10 left-1/4 w-16 h-16 border border-white/20 rounded-full"></div>
          <div className="absolute bottom-20 right-1/3 w-20 h-20 bg-white/10 rounded-full"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <Badge className="bg-golden-500 text-forest-900 px-4 py-2 text-lg font-semibold mb-4">
                ✨ Interactive Children's Book
              </Badge>
              <h1 className="text-5xl md:text-7xl font-bold font-playful mb-6 leading-tight">
                Where the Brave Things Grow
              </h1>
              <p className="text-xl md:text-2xl text-white/90 max-w-4xl mx-auto leading-relaxed">
                Stories, Science & Activities for Raising Mindful, Resilient Kids
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="max-w-2xl mx-auto"
            >
              <p className="text-lg text-white/80 leading-relaxed">
                Experience Chapter 1 for free and discover how Mila the Mouse learns mindfulness when her adventure becomes overwhelming. 
                <span className="font-semibold"> No commitment required—just curiosity and wonder.</span>
              </p>
            </motion.div>
          </div>

          {/* Read Chapter 1 Free Button - Bottom of Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center"
          >
            <Button 
              size="lg" 
              className="bg-white hover:bg-cream-50 text-forest-700 px-10 py-6 text-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300"
              asChild
            >
              <Link to="/signup?redirect=/book/wtbtg&demo=true" onClick={scrollToTop}>
                Read Chapter 1 Free
                <Play className="w-6 h-6 ml-3" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Section 2: Meet Mila the Mouse - Character Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Users className="w-8 h-8 text-golden-500" />
              <h2 className="text-4xl md:text-5xl font-bold text-forest-900 font-playful">
                Experience Chapter 1: The Still Pond
              </h2>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover how Mila and her forest friends learn the power of mindfulness when their adventure starts to feel overwhelming. 
              <span className="font-semibold text-forest-700"> Read it free—no commitment, just curiosity.</span>
            </p>
          </motion.div>

          {/* Character Grid - 2x2 on desktop, 1 column on mobile */}
          <div className="grid md:grid-cols-2 gap-8">
            {characters.map((character, index) => (
              <DemoCharacterCard
                key={index}
                name={character.name}
                description={character.description}
                emoji={character.emoji}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Educational Components - NEW SECTION */}
      <section className="py-20 bg-cream-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <GraduationCap className="w-8 h-8 text-golden-500" />
              <h2 className="text-4xl md:text-5xl font-bold text-forest-900 font-playful">
                🎯 Educational Components
              </h2>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Evidence-based learning components designed by child development experts to maximize educational impact.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Science Connection",
                description: "Brain science explanations in kid-friendly language",
                icon: Sparkles,
                details: "Each chapter explains how the brain works and why these skills help kids focus, feel calm, and handle big emotions."
              },
              {
                title: "Try This Activity",
                description: "Guided exercises and hands-on practice",
                icon: Play,
                details: "Interactive activities that let children practice new skills immediately with step-by-step guidance."
              },
              {
                title: "Family Discussion",
                description: "Thoughtful questions for meaningful conversations",
                icon: Users,
                details: "Conversation starters that help families talk about emotions, challenges, and growth together."
              },
              {
                title: "Adult Guide",
                description: "Research-backed guidance for parents and caregivers",
                icon: BookOpen,
                details: "Evidence-based tips and strategies to support children's social-emotional development at home."
              }
            ].map((component, index) => {
              const Icon = component.icon;
              return (
                <motion.div
                  key={component.title}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div className="border-2 border-forest-200 hover:border-forest-300 hover:shadow-lg transition-all duration-300 h-full bg-gradient-to-br from-white to-cream-50 rounded-lg p-8">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-forest-100 border-2 border-forest-200 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-forest-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-forest-900 mb-2">{component.title}</h3>
                        <p className="text-gray-600 mb-4 leading-relaxed">{component.description}</p>
                        <p className="text-sm text-gray-500 leading-relaxed">{component.details}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 3: Ready to Begin CTA */}
      <section className="py-20 bg-gradient-to-br from-golden-50 to-cream-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Decorative element */}
            <div className="mb-8">
              <Sparkles className="w-16 h-16 mx-auto text-golden-500" />
            </div>

            <h2 className="text-4xl md:text-5xl font-bold font-playful text-forest-900 mb-6">
              ✨ Ready to Begin the Adventure?
            </h2>
            
            <p className="text-xl text-gray-700 mb-4">
              Experience the magic of <span className="font-bold">Chapter 1: The Still Pond</span>
            </p>
            
            <p className="text-lg text-gray-600 mb-8">
              See why thousands of families love this interactive story
            </p>

            {/* Large CTA Button */}
            <Button 
              size="lg" 
              className="bg-forest-600 hover:bg-forest-700 text-white px-12 py-6 text-xl font-semibold mb-8"
              asChild
            >
              <Link to="/signup?redirect=/book/wtbtg&demo=true" onClick={scrollToTop}>
                Read Chapter 1 Free
                <Play className="w-6 h-6 ml-3" />
              </Link>
            </Button>

            {/* Feature Badges */}
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Ages 3-8</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>10-15 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Interactive activities</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>No signup required</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
